import { motion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import MarketingNav from "@/components/layout/MarketingNav";

/**
 * Student + partner password recovery landing page.
 * Must live at `/auth/recovery` (allowlisted redirect + AASA path).
 * Exchanges the PKCE `code` then lets the user set a new password.
 */
const AuthRecovery = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const errorDescription =
        searchParams.get("error_description") || searchParams.get("error");
      if (errorDescription) {
        if (!cancelled) {
          setLinkError(decodeURIComponent(errorDescription.replace(/\+/g, " ")));
          setBootstrapping(false);
        }
        return;
      }

      const code = searchParams.get("code");
      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            if (!cancelled) {
              setLinkError(
                error.message ||
                  "This reset link is invalid or has expired. Request a new one from the app.",
              );
            }
            return;
          }
          // Clean the code out of the URL without losing the session.
          window.history.replaceState({}, "", "/auth/recovery");
        } else {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            if (!cancelled) {
              setLinkError(
                "Open the link from your reset email, or request a new password reset.",
              );
            }
            return;
          }
        }
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : "Could not verify reset link.";
        if (!cancelled) setLinkError(message);
        return;
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast.error(error.message || "Failed to reset password. Try again.");
        return;
      }

      setSuccess(true);
      toast.success("Password updated.");
      setTimeout(() => {
        navigate("/partners/login", { replace: true });
      }, 2500);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "An error occurred.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-950">
      <MarketingNav />

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 lg:p-16 bg-[#f8faf9] dark:bg-gray-950">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {bootstrapping ? (
            <div className="text-center py-16">
              <div className="h-8 w-8 rounded-full border-2 border-skutopia-500 border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Verifying your reset link…
              </p>
            </div>
          ) : linkError ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 p-6 sm:p-8">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Link expired or invalid
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                {linkError}
              </p>
              <Button asChild className="w-full">
                <Link to="/partners/forgot-password">Request a new link</Link>
              </Button>
            </div>
          ) : success ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 p-6 sm:p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-skutopia-600 mx-auto mb-4" />
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Password updated
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                You can sign in with your new password in the Skutopia app or
                partner portal.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Set a new password
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Choose a strong password you have not used here before.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 shadow-xl shadow-gray-200/50 dark:shadow-none p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                    >
                      New password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                        placeholder="At least 8 characters"
                        required
                        minLength={8}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="confirm"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                    >
                      Confirm password
                    </label>
                    <input
                      id="confirm"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                      placeholder="Repeat password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Saving…" : "Save new password"}
                  </Button>
                </form>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AuthRecovery;
